import { useState, useEffect, useMemo, useRef } from "react";
import { fetchServedStates } from "DBServices/firestoreDBService";

/**
 * Loads the admin-managed list of served states and their neighborhood areas.
 *
 * Returns:
 *  - states: array of { firestoreId, name, neighborhoods: string[] }
 *  - neighborhoodsByState: { [stateName]: string[] }
 *  - allNeighborhoods: flattened, de-duplicated list of every neighborhood
 *  - loading / error: request status
 *  - reload: re-fetch function
 */
export default function useServedLocations() {
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const mountedRef = useRef(false);

    const reload = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchServedStates();
            if (mountedRef.current) {
                setStates(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error("useServedLocations load failed:", e);
            if (mountedRef.current) {
                setError(e.message || "Failed to load served locations");
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        reload();
        return () => {
            mountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const neighborhoodsByState = useMemo(() => {
        const map = {};
        states.forEach((s) => {
            map[s.name] = Array.isArray(s.neighborhoods) ? s.neighborhoods : [];
        });
        return map;
    }, [states]);

    const allNeighborhoods = useMemo(() => {
        const set = new Set();
        states.forEach((s) => {
            (s.neighborhoods || []).forEach((n) => set.add(n));
        });
        return Array.from(set);
    }, [states]);

    return {
        states,
        neighborhoodsByState,
        allNeighborhoods,
        loading,
        error,
        reload,
    };
}
